<template>
  <div v-if="isMobile" class="space-y-2">
    <NavLink
      v-for="item in filteredNavItems"
      :key="item.to"
      v-bind="item"
      :isMobile="true"
    />
  </div>
  
  <div v-else ref="scrollContainer" class="hidden md:block relative group">
    <!-- Left fade and button -->
    <div 
      v-show="canScrollLeft"
      class="absolute left-0 top-0 bottom-0 flex items-center"
    >
      <div class="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white dark:from-black to-transparent z-10 pointer-events-none" />
      <button
        @click="scroll('left')"
        class="h-8 w-8 rounded-full bg-white dark:bg-[#111] shadow-lg border border-slate-200 dark:border-gray-700 flex items-center justify-center z-20 -ml-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-slate-50 dark:hover:bg-[#222]"
        aria-label="Scroll left"
      >
        <ChevronLeft class="h-5 w-5 text-slate-600 dark:text-white" />
      </button>
    </div>
    
    <!-- Navigation links -->
    <div 
      ref="scrollContent"
      class="flex items-center overflow-x-auto scrollbar-none gap-8 px-4 scroll-smooth mx-auto"
      @scroll="checkScroll"
    >
      <NavLink
        v-for="item in filteredNavItems"
        :key="item.to"
        v-bind="item"
        :isMobile="false"
        class="shrink-0"
      />
    </div>
    
    <!-- Right fade and button -->
    <div 
      v-show="canScrollRight"
      class="absolute right-0 top-0 bottom-0 flex items-center"
    >
      <div class="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white dark:from-black to-transparent z-10 pointer-events-none" />
      <button
        @click="scroll('right')"
        class="h-8 w-8 rounded-full bg-white dark:bg-[#111] shadow-lg border border-slate-200 dark:border-gray-700 flex items-center justify-center z-20 -mr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-slate-50 dark:hover:bg-[#222]"
        aria-label="Scroll right"
      >
        <ChevronRight class="h-5 w-5 text-slate-600 dark:text-white" />
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { useRouter, RouteRecordRaw } from 'vue-router';
import NavLink from './NavLink.vue';
import { NAV_ITEMS } from '../config/navigation';
import { useAuth } from '@/shared/composables/useAuth';
import { useAuth0 } from '@auth0/auth0-vue';

const props = defineProps<{
  isMobile?: boolean;
}>();

const router = useRouter();
const { isAuthed } = useAuth();
const { user } = useAuth0();
const scrollContent = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

const userRoles = computed(() => {
  if (isAuthed.value && user.value) {
    const roles = user.value[`${import.meta.env.VITE_ZERO_AUDIENCE}/roles`] || [];
    return roles.length === 0 ? ['user'] : roles;
  }
  return [];
});

const hasRequiredRole = (requiredRoles: string[], userRoles: string[]) => {
  // Admin always has access
  if (userRoles.includes('admin')) return true;
  // Check if user has any of the required roles
  return requiredRoles.some(role => userRoles.includes(role));
};

const filteredNavItems = computed(() => 
  NAV_ITEMS.filter(item => {
    // Basic filters
    if (props.isMobile && item.hideForLargeScreen) return false;
    
    // Get route meta
    const route = router.getRoutes().find(r => r.path === item.to);
    if (!route) return true; // If no route found, show the nav item
    
    // Check auth requirements from route meta
    if (route.meta?.requiresAuth && !isAuthed.value) return false;
    
    // Check role requirements from route meta
    const requiredRoles = route.meta?.requiredRoles as string[] | undefined;
    if (requiredRoles?.length) {
      const roles = userRoles.value;
      if (!hasRequiredRole(requiredRoles, roles)) {
        return false;
      }
    }
    
    return true;
  })
);

const checkScroll = () => {
  if (!scrollContent.value) return;
  
  const { scrollLeft, scrollWidth, clientWidth } = scrollContent.value;
  canScrollLeft.value = scrollLeft > 0;
  canScrollRight.value = scrollLeft < scrollWidth - clientWidth - 1; // -1 for rounding errors
};

const scroll = (direction: 'left' | 'right') => {
  if (!scrollContent.value) return;
  
  const scrollAmount = 200; // Adjust this value to control scroll distance
  const currentScroll = scrollContent.value.scrollLeft;
  const targetScroll = direction === 'left' 
    ? currentScroll - scrollAmount 
    : currentScroll + scrollAmount;
    
  scrollContent.value.scrollTo({
    left: targetScroll,
    behavior: 'smooth'
  });
};

// Check on mount and when items change
onMounted(() => {
  checkScroll();
  window.addEventListener('resize', checkScroll);
});
</script>

<style scoped>
.scrollbar-none {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
.scrollbar-none::-webkit-scrollbar {
  display: none;  /* Chrome, Safari and Opera */
}

.scroll-smooth {
  scroll-behavior: smooth;
}
</style>